import React, { useMemo, useState, useEffect } from "react";
import Select from "react-select";
import axiosInstance from "../axiosinstance";
import { Snackbar, Alert as MuiAlert } from "@mui/material";

export default function FeeCalculator({ isSidebarExpanded }) {
  const [entityTypes, setEntityTypes] = useState([]);
  const [isEntityTypesLoading, setIsEntityTypesLoading] = useState(false);
  const [entityTypesError, setEntityTypesError] = useState("");

  const [selectedEntityType, setSelectedEntityType] = useState(null);
  const [selectedEntityTypeId, setSelectedEntityTypeId] = useState("");
  const [requiresPartnerCount, setRequiresPartnerCount] = useState(false);

  const [partnerCount, setPartnerCount] = useState("");
  const [turnover, setTurnover] = useState("");

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [feePreviewData, setFeePreviewData] = useState(null);
  const [feeSummary, setFeeSummary] = useState(null);
  const [summaryError, setSummaryError] = useState("");
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [serviceSelectionError, setServiceSelectionError] = useState("");
  const [selectedServicesForSla, setSelectedServicesForSla] = useState([]);
  const [isSlaSubmitting, setIsSlaSubmitting] = useState(false);
  const [clients, setClients] = useState([]);
  const [isClientsLoading, setIsClientsLoading] = useState(false);
  const [selectedSlaClient, setSelectedSlaClient] = useState(null);
  const [isGenerateSlaModalOpen, setIsGenerateSlaModalOpen] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [isSlaPreviewModalOpen, setIsSlaPreviewModalOpen] = useState(false);
  const [slaClientError, setSlaClientError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saveSuccessMessage, setSaveSuccessMessage] = useState("");
  const [isSlaSaved, setIsSlaSaved] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [savedAgreementId, setSavedAgreementId] = useState("");
  const [savedAgreementNo, setSavedAgreementNo] = useState("");
  const [savedClientName, setSavedClientName] = useState("");
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [agreementHtml, setAgreementHtml] = useState("");
  const [agreementPreviewLoading, setAgreementPreviewLoading] = useState(false);
  const [agreementPreviewError, setAgreementPreviewError] = useState("");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const clearSummary = () => {
    setFeePreviewData(null);
    setFeeSummary(null);
    setSummaryError("");
    setSelectedServicesForSla([]);
    setPreviewRows([]);
    setSavedAgreementId("");
    setSavedAgreementNo("");
    setSavedClientName("");
    setIsSlaPreviewModalOpen(false);
    setAgreementHtml("");
    setAgreementPreviewError("");
    setSaveError("");
    setSaveSuccessMessage("");
    setIsSlaSaved(false);
  };

  const formatCurrency = (value) => {
    const amount = Number(value || 0);
    return `Rs ${amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatSummaryAmount = (value) => {
    const amount = Number(value ?? 0);
    const normalized = Number.isFinite(amount) ? Math.trunc(amount) : 0;
    return normalized.toLocaleString("en-IN");
  };

  const toMoneyString = (value) => {
    const amount = Number.parseFloat(value ?? 0);
    return (Number.isNaN(amount) ? 0 : amount).toFixed(2);
  };

  useEffect(() => {
    const fetchEntityTypes = async () => {
      setIsEntityTypesLoading(true);
      setEntityTypesError("");
      try {
        const response = await axiosInstance.get(`/api/entities`);
        const types = Array.isArray(response.data?.data) ? response.data.data : [];
        setEntityTypes(types);
      } catch (error) {
        console.error("Error fetching entity types:", error);
        setEntityTypes([]);
        setEntityTypesError("Failed to fetch entity types");
      } finally {
        setIsEntityTypesLoading(false);
      }
    };

    fetchEntityTypes();
  }, []);

  useEffect(() => {
    const fetchClients = async () => {
      setIsClientsLoading(true);
      try {
        const response = await axiosInstance.get(`/api/get_client_name`);
        const clientData = Array.isArray(response.data?.data) ? response.data.data : [];
        setClients(clientData);
        console.log("clientData", clientData)
      } catch (error) {
        console.error("Error fetching clients:", error);
        setClients([]);
      } finally {
        setIsClientsLoading(false);
      }
    };

    fetchClients();
  }, []);

  const entityTypeOptions = useMemo(
    () =>
      entityTypes.map((entity) => ({
        label: entity.entity_name,
        value: entity.entity_type_id,
        entity_name: entity.entity_name,
        entity_type_id: entity.entity_type_id,
        requires_partner_count: Number(entity.requires_partner_count) === 1 ? 1 : 0,
      })),
    [entityTypes]
  );

  const handleEntityTypeChange = (option) => {
    clearSummary();
    setSelectedEntityType(option);
    setSelectedEntityTypeId(option?.value || "");

    const partnerRequired = Number(option?.requires_partner_count || 0) === 1;
    setRequiresPartnerCount(partnerRequired);

    if (!partnerRequired) {
      setPartnerCount("");
    }

    setErrors((prev) => ({
      ...prev,
      selectedEntityType: "",
      partnerCount: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedEntityTypeId) newErrors.selectedEntityType = "Entity type is required";

    if (!turnover.trim()) {
      newErrors.turnover = "Turnover is required";
    } else if (isNaN(turnover)) {
      newErrors.turnover = "Turnover must be a number";
    } else if (Number(turnover) < 0) {
      newErrors.turnover = "Turnover cannot be negative";
    }

    if (requiresPartnerCount) {
      if (!String(partnerCount).trim()) {
        newErrors.partnerCount = "Partner count is required";
      } else if (!/^[0-9]+$/.test(String(partnerCount)) || Number(partnerCount) < 1) {
        newErrors.partnerCount =
          "Partner count must be a number greater than or equal to 1";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildCalculationPayload = () => ({
    entity_type_id: selectedEntityTypeId,
    turnover: Number(turnover),
    ...(requiresPartnerCount ? { partner_count: Number(partnerCount) } : {}),
  });

  const mapSummaryFromResponse = (responseData) => {
    const responsePayload = responseData || {};
    const responseDataSection = responsePayload?.data || {};
    const breakdown = Array.isArray(responsePayload?.fee_breakdown)
      ? responsePayload.fee_breakdown
      : Array.isArray(responseDataSection?.fee_breakdown)
        ? responseDataSection.fee_breakdown
        : [];

    const computedGrandTotal = breakdown.reduce(
      (sum, row) => sum + Number.parseFloat(row?.total_fee ?? 0),
      0
    );

    return {
      entityName:
        responseDataSection?.entity_type?.entity_name ||
        selectedEntityType?.entity_name ||
        selectedEntityType?.label ||
        "NA",
      turnover: responseDataSection?.inputs?.turnover ?? Number(turnover),
      partnerCount:
        responseDataSection?.inputs?.partner_count ??
        (requiresPartnerCount ? Number(partnerCount) : null),
      servicesCount: breakdown.length,
      feeBreakdown: breakdown,
      grandTotal: Number.parseFloat(
        responsePayload?.grand_total ?? responseDataSection?.grand_total ?? computedGrandTotal
      ),
    };
  };

  const handleSubmit = async () => {
    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);
    setSummaryError("");

    const payload = buildCalculationPayload();

    try {
      const response = await axiosInstance.post(`/api/calculate_fees`, payload);
      setFeePreviewData(response.data || null);
      setFeeSummary(mapSummaryFromResponse(response.data));
      setSelectedServicesForSla([]);
      setPreviewRows([]);
      setSavedAgreementId("");
      setSavedAgreementNo("");
      setSavedClientName("");
    } catch (error) {
      console.error("Error calculating fee:", error);
      setFeePreviewData(null);
      setFeeSummary(null);
      setSummaryError(error.response?.data?.message || "Failed to calculate fee.");
      setSnackbarSeverity("error");
      setSnackbarMessage(error.response?.data?.message || "Failed to calculate fee.");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    window.history.back();
  };

  const handleGenerateSla = () => {
    if (!feeSummary || !feePreviewData) return;
    const allRuleIds = (feeSummary.feeBreakdown || [])
      .map((service) => service.rule_id)
      .filter(Boolean);
    setSelectedServiceIds(allRuleIds);
    setServiceSelectionError("");
    setIsServiceModalOpen(true);
  };

  const handleServiceToggle = (ruleId) => {
    setSelectedServiceIds((prev) =>
      prev.includes(ruleId) ? prev.filter((id) => id !== ruleId) : [...prev, ruleId]
    );
    setServiceSelectionError("");
  };

  const handleConfirmServiceSelection = async () => {
    if (selectedServiceIds.length === 0) {
      setServiceSelectionError("Please select at least one service.");
      return;
    }

    setIsSlaSubmitting(true);
    try {
      const selectedServices = (feeSummary?.feeBreakdown || []).filter((service) =>
        selectedServiceIds.includes(service.rule_id)
      );
      setSelectedServicesForSla(selectedServices);

      const editableRows = selectedServices.map((item) => {
        const baseFee = Number.parseFloat(item.base_fee ?? 0);
        const countFee = Number.parseFloat(item.count_fee ?? 0);
        return {
          rule_id: item.rule_id,
          service_name: item.service_name || "NA",
          base_fee: Number.isNaN(baseFee) ? 0 : baseFee,
          count_fee: Number.isNaN(countFee) ? 0 : countFee,
          total_fee:
            Number.parseFloat(item.total_fee ?? 0) ||
            (Number.isNaN(baseFee) ? 0 : baseFee) + (Number.isNaN(countFee) ? 0 : countFee),
        };
      });

      setPreviewRows(editableRows);
      setSelectedSlaClient(null);
      setSaveError("");
      setSaveSuccessMessage("");
      setSavedAgreementId("");
      setSavedAgreementNo("");
      setSavedClientName("");
      setIsSlaSaved(false);

      setSnackbarSeverity("success");
      setSnackbarMessage("Generate SLA ready.");
      setSnackbarOpen(true);
      setIsServiceModalOpen(false);
      setIsGenerateSlaModalOpen(true);
    } catch (error) {
      console.error("Error generating SLA preview:", error);
      setSnackbarSeverity("error");
      setSnackbarMessage(
        error.response?.data?.message || "Failed to generate SLA preview."
      );
      setSnackbarOpen(true);
    } finally {
      setIsSlaSubmitting(false);
    }
  };

  const handleSlaFeeChange = (ruleId, field, rawValue) => {
    setSaveError("");
    setSaveSuccessMessage("");
    setIsSlaSaved(false);
    const nextValue = rawValue === "" ? "" : rawValue.replace(/[^0-9.]/g, "");
    setPreviewRows((prev) =>
      prev.map((row) => {
        if (row.rule_id !== ruleId) return row;
        const updatedRow = {
          ...row,
          [field]: nextValue === "" ? "" : Number.parseFloat(nextValue),
        };
        const baseFee = Number.parseFloat(updatedRow.base_fee || 0);
        const countFee = Number.parseFloat(updatedRow.count_fee || 0);
        updatedRow.total_fee = (Number.isNaN(baseFee) ? 0 : baseFee) + (Number.isNaN(countFee) ? 0 : countFee);
        return updatedRow;
      })
    );
  };

  const handleSlaFeeFocus = (ruleId, field) => {
    setPreviewRows((prev) =>
      prev.map((row) => {
        if (row.rule_id !== ruleId) return row;
        if (Number(row[field] || 0) !== 0) return row;
        const updatedRow = { ...row, [field]: "" };
        const baseFee = Number.parseFloat(updatedRow.base_fee || 0);
        const countFee = Number.parseFloat(updatedRow.count_fee || 0);
        updatedRow.total_fee =
          (Number.isNaN(baseFee) ? 0 : baseFee) + (Number.isNaN(countFee) ? 0 : countFee);
        return updatedRow;
      })
    );
  };

  const slaGrandTotal = useMemo(
    () =>
      previewRows.reduce(
        (sum, row) => sum + Number.parseFloat(row.total_fee || 0),
        0
      ),
    [previewRows]
  );

  const handleSlaClientChange = (option) => {
    setSelectedSlaClient(option);
    setSlaClientError("");
    setSaveError("");
    setSaveSuccessMessage("");
    setIsSlaSaved(false);
  };

  const handlePreviewSla = () => {
    if (!savedAgreementId) {
      setSaveError("Please save SLA first.");
      return;
    }
    setIsGenerateSlaModalOpen(false);
    setIsSlaPreviewModalOpen(true);
  };

  const handleSaveSla = async () => {
    setSaveError("");
    setSaveSuccessMessage("");
    setSlaClientError("");

    if (!selectedSlaClient) {
      setSlaClientError("Please select a client before saving.");
      setSaveError("Client is required.");
      return;
    }
    if (!selectedEntityTypeId) {
      setSaveError("Entity type is required.");
      return;
    }
    if (!String(turnover).trim() || Number.isNaN(Number(turnover))) {
      setSaveError("Turnover is required and must be numeric.");
      return;
    }
    if (requiresPartnerCount) {
      const parsedPartnerCount = Number(partnerCount);
      if (!String(partnerCount).trim() || Number.isNaN(parsedPartnerCount) || parsedPartnerCount < 1) {
        setSaveError("Partner count is required and must be at least 1.");
        return;
      }
    }
    if (!Array.isArray(previewRows) || previewRows.length === 0) {
      setSaveError("At least one item is required before saving.");
      return;
    }

    const originalRowsByRuleId = new Map(
      (feeSummary?.feeBreakdown || []).map((row) => [row.rule_id, row])
    );

    const itemsPayload = previewRows.map((row) => {
      const baseFee = Number.parseFloat(row.base_fee || 0);
      const countFee = Number.parseFloat(row.count_fee || 0);
      const editedTotal =
        (Number.isNaN(baseFee) ? 0 : baseFee) + (Number.isNaN(countFee) ? 0 : countFee);
      const originalRow = originalRowsByRuleId.get(row.rule_id);
      const calculatedFee = Number.parseFloat(originalRow?.total_fee ?? 0);

      return {
        rule_id: row.rule_id,
        service_name: row.service_name || "",
        base_fee: toMoneyString(baseFee),
        count_fee: toMoneyString(countFee),
        calculated_fee: toMoneyString(calculatedFee),
        edited_fee: toMoneyString(editedTotal),
        final_fee: toMoneyString(editedTotal),
      };
    });

    const calculatedTotal = itemsPayload.reduce(
      (sum, item) => sum + Number.parseFloat(item.calculated_fee || 0),
      0
    );
    const finalTotal = itemsPayload.reduce(
      (sum, item) => sum + Number.parseFloat(item.final_fee || 0),
      0
    );

    const savePayload = {
      client_id: selectedSlaClient.value,
      entity_type_id: selectedEntityTypeId,
      turnover: Number(turnover),
      partner_count: requiresPartnerCount ? Number(partnerCount) : 0,
      calculated_total: toMoneyString(calculatedTotal),
      final_total: toMoneyString(finalTotal),
      client_snapshot: {
        name: selectedSlaClient?.client_name || selectedSlaClient?.label || "",
        entity_type: selectedEntityType?.entity_name || selectedEntityType?.label || "",
        company_name:
          selectedSlaClient?.company_name ||
          selectedSlaClient?.client_name ||
          selectedSlaClient?.label ||
          "",
        address: selectedSlaClient?.shipping_address || "",
        pan: selectedSlaClient?.pan_number || "",
        email: selectedSlaClient?.contact_email || "",
      },
      items: itemsPayload,
    };

    setIsSaveLoading(true);
    try {
      console.log("[FeeCalculator] service-agreement save payload", savePayload);
      const response = await axiosInstance.post(`/api/service-agreement/save`, savePayload);
      console.log("[FeeCalculator] service-agreement save response", response.data);

      const data = response.data?.data || {};
      setSavedAgreementId(data.agreement_id || "");
      setSavedAgreementNo(data.agreement_no || "");
      setSavedClientName(selectedSlaClient?.label || selectedSlaClient?.client_name || "Client");
      setSaveSuccessMessage("");
      setIsSlaSaved(true);
      setSnackbarSeverity("success");
      setSnackbarMessage("Service agreement saved successfully");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error saving service agreement:", error);
      setSaveError(
        error.response?.data?.message || "Failed to save service agreement."
      );
    } finally {
      setIsSaveLoading(false);
    }
  };

  useEffect(() => {
    const fetchAgreementPreview = async () => {
      if (!isSlaPreviewModalOpen || !savedAgreementId) return;
      setAgreementPreviewLoading(true);
      setAgreementPreviewError("");
      try {
        const response = await axiosInstance.get(
          `/api/service-agreement/${savedAgreementId}/preview`,
          { responseType: "text" }
        );
        const html = typeof response.data === "string" ? response.data : "";
        setAgreementHtml(html);
      } catch (error) {
        console.error("Error loading agreement preview HTML:", error);
        setAgreementPreviewError(
          error.response?.data?.message || "Failed to load agreement preview."
        );
        setAgreementHtml("");
      } finally {
        setAgreementPreviewLoading(false);
      }
    };

    fetchAgreementPreview();
  }, [isSlaPreviewModalOpen, savedAgreementId]);

  const handleCloseGenerateSlaModal = () => {
    setIsGenerateSlaModalOpen(false);
    setSlaClientError("");
    setSaveError("");
    setSaveSuccessMessage("");
    setIsSlaSaved(false);
    setIsSlaPreviewModalOpen(false);
    setAgreementHtml("");
    setAgreementPreviewError("");
  };

  const handleClosePreviewModal = () => {
    setIsSlaPreviewModalOpen(false);
    setIsGenerateSlaModalOpen(false);
  };

  const handleBackToServiceSelection = () => {
    setIsGenerateSlaModalOpen(false);
    setIsServiceModalOpen(true);
    setSlaClientError("");
    setSaveError("");
    setSaveSuccessMessage("");
    setIsSlaSaved(false);
    setIsSlaPreviewModalOpen(false);
  };

  const handleDownloadAgreementPdf = async () => {
    const agreementId = savedAgreementId;
    if (!agreementId) {
      setSaveError("Please save SLA first.");
      return;
    }

    setIsDownloadLoading(true);
    try {
      const response = await axiosInstance.get(
        `/api/service-agreement/${agreementId}/download`,
        { responseType: "blob" }
      );
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Service-Agreement-${savedClientName || "Client"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading service agreement:", error);
      setSaveError("Failed to download service agreement.");
    } finally {
      setIsDownloadLoading(false);
    }
  };

  return (
    <div className={`table-container fee-calculator-create-page ${isSidebarExpanded ? "expanded" : "collapsed"}`}>
      <div className="table-container-content form-main-body fee-calculator-create-content">
        <div className="fee-create-page-header">
          <div>
            <h1 className="form-title">Fee Calculator</h1>
            <p className="fee-create-subtitle">
              Select entity type and turnover to calculate applicable fees.
            </p>
          </div>
        </div>

        <div className="fee-calculator-layout fee-calculator-layout-compact">
          <div className="fee-calculator-left">
            <div className="fee-calculator-form-card">
              <div className="form-group fee-compact-group">
                <label className="form-label">
                  Entity Type <span className="required">*</span>
                </label>
                <div className="form-inputs">
                  <Select
                    className={`select-field ${errors.selectedEntityType ? "input-error" : ""}`}
                    value={selectedEntityType}
                    onChange={handleEntityTypeChange}
                    options={entityTypeOptions}
                    placeholder={
                      isEntityTypesLoading ? "Loading entity types..." : "Select Entity Type"
                    }
                    isClearable
                    isLoading={isEntityTypesLoading}
                  />
                  {errors.selectedEntityType && (
                    <p className="error-message">{errors.selectedEntityType}</p>
                  )}
                  {entityTypesError && (
                    <p className="error-message">{entityTypesError}</p>
                  )}
                </div>
              </div>

              {requiresPartnerCount && (
                <div className="form-group fee-compact-group">
                  <label className="form-label">
                    Partner Count <span className="required">*</span>
                  </label>
                  <div className="form-inputs">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      className="input-field"
                      placeholder="Enter Partner Count"
                      value={partnerCount}
                      onChange={(e) => {
                        clearSummary();
                        const nextValue = e.target.value.replace(/[^0-9]/g, "");
                        setPartnerCount(nextValue);
                        setErrors((prev) => ({ ...prev, partnerCount: "" }));
                      }}
                    />
                    {errors.partnerCount && (
                      <p className="error-message">{errors.partnerCount}</p>
                    )}
                  </div>
                </div>
              )}

              <div className="form-group fee-compact-group">
                <label className="form-label">
                  Turnover (in crores) <span className="required">*</span>
                </label>
                <div className="form-inputs">
                  <input
                    type="number"
                    className="input-field"
                    placeholder="Enter Turnover Amount"
                    value={turnover}
                    onChange={(e) => {
                      clearSummary();
                      setTurnover(e.target.value);
                      setErrors((prev) => ({ ...prev, turnover: "" }));
                    }}
                  />
                  {errors.turnover && <p className="error-message">{errors.turnover}</p>}
                </div>
              </div>

              <div className="fee-calculator-actions fee-calculator-actions-inline">
                <div className="button-group">
                  <button type="button" className="btn btn-cancel" onClick={handleCancel}>
                    CANCEL
                  </button>
                  <button
                    type="button"
                    className="btn btn-save"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Calculating..." : "CALCULATE FEE"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="fee-calculator-right">
            <div className="fee-summary-card">
              <div className="fee-summary-card-header">
                <div>
                  <h2 className="fee-summary-title">Fee Summary</h2>
                  <p className="fee-summary-subtitle">
                    Calculated fee breakup based on selected entity type and turnover.
                  </p>
                </div>
              </div>

              {!feeSummary && !summaryError && (
                <div className="fee-summary-empty">Calculate fee to view the summary.</div>
              )}

              {summaryError && <div className="fee-summary-error">{summaryError}</div>}

              {feeSummary && (
                <>
                  <div className="fee-summary-meta">
                    <div className="fee-summary-row">
                      <span>Entity Type</span>
                      <strong>{feeSummary.entityName || "NA"}</strong>
                    </div>
                    <div className="fee-summary-row">
                      <span>Turnover</span>
                      <strong>{feeSummary.turnover}</strong>
                    </div>
                    {feeSummary.partnerCount !== null && (
                      <div className="fee-summary-row">
                        <span>Partner Count</span>
                        <strong>{feeSummary.partnerCount}</strong>
                      </div>
                    )}
                    <div className="fee-summary-row">
                      <span>Applicable Services</span>
                      <strong>{feeSummary.servicesCount}</strong>
                    </div>
                  </div>

                  <div className="fee-summary-table-wrap">
                    <table className="table multilevel-data fee-summary-table">
                      <thead>
                        <tr className="table-row">
                          <th>Service Name</th>
                          <th>Base Fee (in Rs.)</th>
                          <th>Count Fee (in Rs.)</th>
                          <th>Total Fee (in Rs.)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feeSummary.feeBreakdown.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="center-align">
                              No fee rows returned
                            </td>
                          </tr>
                        ) : (
                          feeSummary.feeBreakdown.map((item) => (
                            <tr key={item.rule_id}>
                              <td>{item.service_name || "NA"}</td>
                              <td>{formatSummaryAmount(item.base_fee)}</td>
                              <td>{formatSummaryAmount(item.count_fee)}</td>
                              <td>{formatSummaryAmount(item.total_fee)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="fee-summary-grand-total">
                    <span>Grand Total</span>
                    <strong>{formatSummaryAmount(feeSummary.grandTotal)}</strong>
                  </div>
                </>
              )}

              <div className="fee-summary-actions">
                {selectedServicesForSla.length > 0 && (
                  <div className="fee-summary-selected-count">
                    Selected services: {selectedServicesForSla.length}
                  </div>
                )}
                <button
                  type="button"
                  className="btn btn-save"
                  onClick={handleGenerateSla}
                  disabled={!feeSummary}
                >
                  GENERATE SLA
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isServiceModalOpen && (
        <div className="modal-overlay sla-modal">
          <div className="modal-content sla-generate-modal sla-modal-content sla-service-modal">
            <h2>Select Services For SLA</h2>
            <div className="fee-service-selection-list">
              {(feeSummary?.feeBreakdown || []).length === 0 ? (
                <p className="fee-service-selection-empty">No services available.</p>
              ) : (
                (feeSummary?.feeBreakdown || []).map((service) => (
                  <label key={service.rule_id} className="fee-service-selection-item">
                    <input
                      type="checkbox"
                      checked={selectedServiceIds.includes(service.rule_id)}
                      onChange={() => handleServiceToggle(service.rule_id)}
                    />
                    <span>{service.service_name || "Unnamed Service"}</span>
                  </label>
                ))
              )}
            </div>
            {serviceSelectionError && (
              <p className="error-message">{serviceSelectionError}</p>
            )}
            <div className="modal-buttons sla-modal-buttons">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={() => setIsServiceModalOpen(false)}
                disabled={isSlaSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-save"
                onClick={handleConfirmServiceSelection}
                disabled={isSlaSubmitting}
              >
                {isSlaSubmitting ? "Please wait..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isGenerateSlaModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content sla-generate-modal sla-modal-content sla-generate-modern">

            <div className="sla-topbar">

              {/* Header */}
              <div className="sla-modal-header sla-generate-header">
                <button
                  type="button"
                  className="sla-back-btn"
                  onClick={handleBackToServiceSelection}
                  aria-label="Back to Select Services"
                >
                  {"\u2190"}
                </button>

                <h2 className="sla-generate-title">
                  Generate SLA
                </h2>
              </div>


              {/* Client Selection */}
              <div className="form-group fee-compact-group sla-client-row">

                <label className="form-label">
                  Client
                </label>

                <div className="form-inputs">

                  <div className="sla-client-field-wrap">

                    <Select
                      className={`
            select-field 
            sla-client-select
            ${slaClientError ? "sla-client-select-error" : ""}
          `}
                      classNamePrefix="sla-client-select"

                      value={selectedSlaClient}
                      onChange={handleSlaClientChange}

                      options={clients.map(client => ({
                        label: client.client_name,
                        value: client.client_id,
                        ...client
                      }))}

                      isLoading={isClientsLoading}

                      placeholder={
                        isClientsLoading
                          ? "Loading clients..."
                          : "Select Client"
                      }

                      isClearable
                    />

                    {/* Error Message */}
                    <div className="sla-client-error-slot">
                      {slaClientError && (
                        <p className="error-message sla-client-error-message">
                          {slaClientError}
                        </p>
                      )}
                    </div>

                  </div>

                </div>

              </div>

            </div>

            <div className="fee-summary-table-wrap sla-edit-table-wrap sla-generate-table-wrap">
              <table className="table multilevel-data fee-summary-table sla-edit-table sla-modal-table sla-generate-table">
                <thead>
                  <tr className="table-row">
                    <th>Service Name</th>
                    <th>Base Fee</th>
                    <th>Count Fee</th>
                    <th>Total Fee</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="center-align">
                        No fee rows returned
                      </td>
                    </tr>
                  ) : (
                    previewRows.map((item) => (
                      <tr key={item.rule_id}>
                        <td>{item.service_name || "NA"}</td>
                        <td className="center-align">
                          <input
                            type="number"
                            className="sla-edit-input"
                            value={item.base_fee}
                            onFocus={() => handleSlaFeeFocus(item.rule_id, "base_fee")}
                            onChange={(e) =>
                              handleSlaFeeChange(item.rule_id, "base_fee", e.target.value)
                            }
                          />
                        </td>
                        <td className="center-align">
                          <input
                            type="number"
                            className="sla-edit-input"
                            value={item.count_fee}
                            onFocus={() => handleSlaFeeFocus(item.rule_id, "count_fee")}
                            onChange={(e) =>
                              handleSlaFeeChange(item.rule_id, "count_fee", e.target.value)
                            }
                          />
                        </td>
                        <td className="center-align">{formatCurrency(item.total_fee)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="fee-summary-grand-total sla-generate-grand-total">
              <span>Grand Total</span>
              <strong>{formatCurrency(slaGrandTotal)}</strong>
            </div>

            {saveError && <p className="error-message">{saveError}</p>}
            <div className="modal-buttons sla-modal-buttons sla-generate-footer">
              <button
                type="button"
                className="btn btn-cancel sla-generate-btn"
                onClick={handleCloseGenerateSlaModal}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-save sla-generate-btn"
                onClick={isSlaSaved ? handlePreviewSla : handleSaveSla}
                disabled={isSaveLoading}
              >
                {isSaveLoading ? "Saving..." : isSlaSaved ? "View Agreement" : "Save SLA"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isSlaPreviewModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content sla-generate-modal sla-modal-content sla-preview-modal-content">
            <div className="sla-modal-header sla-preview-header">
              <h2 className="sla-preview-title">SLA Preview</h2>
              {savedAgreementNo && (
                <p className="fee-summary-selected-count">{savedAgreementNo}</p>
              )}
            </div>

            <div className="fee-summary-table-wrap sla-edit-table-wrap sla-preview-table-wrap">
              {agreementPreviewLoading ? (
                <div className="fee-summary-empty">Loading agreement preview...</div>
              ) : agreementPreviewError ? (
                <p className="error-message">{agreementPreviewError}</p>
              ) : (
                <iframe
                  srcDoc={agreementHtml}
                  title="Agreement Preview"
                  style={{ width: "100%", height: "500px", border: "none" }}
                />
              )}
            </div>

            <div className="modal-buttons sla-modal-buttons sla-preview-footer">
              <button
                type="button"
                className="btn btn-save"
                onClick={handleDownloadAgreementPdf}
                disabled={isDownloadLoading}
              >
                {isDownloadLoading ? "Downloading..." : "Download"}
              </button>
              <button
                type="button"
                className="btn btn-cancel sla-preview-close-btn"
                onClick={handleClosePreviewModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: 20000 }}
      >
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
}
